import { numberUtils } from './numberUtils';
import stringHelper from './stringHelper';

import PropertyConstants from '../constants/PropertyConstants';

import { DEFAULT_FILTER_VALUE } from '../constants/PremiumConstants';
import { upperFirst } from './stringUtils';

const { PROPERTY_TYPES_BY_CODE } = PropertyConstants;

function upperFirstEveryWord(string: string) {
  const words = string.split(' ');
  return words.map(upperFirst).join(' ');
}

export interface AddressComponents {
  street: string;
  city: string;
  state: string;
  unit?: string;
  unitName?: string;
  name?: string;
  listingName?: string;
  zip: string;
}

interface FloorPlanDetails {
  numBathrooms: number;
  numBedrooms: number;
  squareFeet: number;
  lowPrice: number;
}

type ListingData = { isCommunity: boolean; floorPlanDetails: FloorPlanDetails } & AddressComponents;

interface CurrentProperty {
  isMultifamily: boolean;
  listingTypeCode: number;
  dateLastActivatedTimestamp?: string;
}

export const propertyUtils = {
  formatAddress(addressComponents: AddressComponents) {
    const { street, city, state, unit, unitName, name, listingName, zip } = addressComponents;

    // Transformations
    let formattedStreet = street ? upperFirstEveryWord(street) : '';
    const formattedCity = city ? upperFirstEveryWord(city) : '';
    const formattedState = state ? state.toUpperCase() : '';
    const formattedUnit = (unit || unitName || '').toUpperCase();
    const formattedName = upperFirstEveryWord(name || listingName || '');

    if (formattedUnit) {
      formattedStreet += ` ${formattedUnit}`;
    }

    const formattedAddress = [formattedStreet, formattedCity, formattedState].filter((val) => val).join(', ');
    const formattedCityState = [formattedCity, formattedState].filter((val) => val).join(', ');
    const formattedCityStateZip = `${formattedCityState} ${zip}`;
    const formattedCompleteAddress = [`${formattedStreet},`, formattedCityState, zip].filter((val) => val).join(' ');

    return {
      formattedAddress,
      formattedCityState,
      formattedCityStateZip,
      formattedCompleteAddress,
      formattedName,
      formattedStreet,
    };
  },
  formatPropertyInfo(floorPlanDetails: FloorPlanDetails) {
    const { numBathrooms, numBedrooms, squareFeet, lowPrice } = floorPlanDetails;

    const fields = [
      ...(numBedrooms ? [`${numBedrooms} ${numBedrooms === 1 ? 'bed' : 'beds'}`] : []),
      ...(numBathrooms ? [`${numBathrooms} ${numBathrooms === 1 ? 'bath' : 'baths'}`] : []),
      ...(squareFeet ? [`${squareFeet} sqft`] : []),
      ...(lowPrice ? [`${numberUtils.asCurrency(lowPrice)}/month`] : []),
    ];

    return fields.join(' · ');
  },
  /**
   * Consists of 2 lines:
   *
   * If Multi-family listing:
   *  - First Line: name OR fall back to street name
   *  - Second Line: Full Address
   *
   * If Single-family listing:
   *  - If no property info (ie. beds, bath, sqft, price)
   *      - First line: Street Name
   *      - Second Line: Full Address
   *  - if more than one property info field
   *      - First Line: Full Address
   *      - Second Line: Property Info
   */
  getPropertyInfoLines(listingData: ListingData) {
    let firstLine;
    let secondLine;

    const { isCommunity, floorPlanDetails } = listingData;

    const { formattedStreet, formattedAddress, formattedName } = this.formatAddress(listingData);

    if (isCommunity) {
      firstLine = formattedName || formattedStreet;
      secondLine = formattedAddress;
    } else {
      const { numBathrooms, numBedrooms, squareFeet, lowPrice } = floorPlanDetails;

      if (!numBathrooms && !numBedrooms && !squareFeet && !lowPrice) {
        firstLine = formattedStreet;
        secondLine = formattedAddress;
      } else {
        firstLine = formattedAddress;
        secondLine = this.formatPropertyInfo(floorPlanDetails);
      }
    }

    return { firstLine, secondLine };
  },
  getListingId(feedId: number, feedListingId: number) {
    return `${feedListingId}_${feedId}`;
  },
  /**
   * getListingIdByPath
   *
   * @param {string} pathname
   *
   * @returns {string}  4btn2kr0nvhbz
   *
   * expects pathname format /rental-manager/properties/4btn2kr0nvhbz/listing
   */
  getListingIdByPath(pathname: string) {
    return pathname.split('/')[3];
  },
  isSingleFamilyRental(currentProperty: CurrentProperty) {
    const { isMultifamily, listingTypeCode } = currentProperty;

    const response =
      !isMultifamily &&
      // type code 1 = Rental
      listingTypeCode === 1;

    return response;
  },
  isRoomForRent({ currentProperty }: { currentProperty: Partial<CurrentProperty> }) {
    const { listingTypeCode } = currentProperty;
    return listingTypeCode === 5;
  },
  formatSearchString(beds: string | number, propertyType: string | number, searchArea: string, capitalize = false) {
    let searchString;
    switch (true) {
      case beds === DEFAULT_FILTER_VALUE && propertyType === DEFAULT_FILTER_VALUE:
        searchString = `all rentals in ${searchArea}`;
        break;
      case propertyType === DEFAULT_FILTER_VALUE:
        searchString = `${beds} bedroom rentals in ${searchArea}`;
        break;
      case beds === DEFAULT_FILTER_VALUE:
        searchString = `${PROPERTY_TYPES_BY_CODE[
          propertyType as keyof typeof PROPERTY_TYPES_BY_CODE
        ].toLowerCase()}s in ${searchArea}`;
        break;
      default:
        searchString = `${beds} bedroom ${PROPERTY_TYPES_BY_CODE[
          propertyType as keyof typeof PROPERTY_TYPES_BY_CODE
        ].toLowerCase()}s in ${searchArea}`;
    }
    if (capitalize) {
      return stringHelper.capitalize(searchString);
    }
    return searchString;
  },
};
